/* eslint-disable no-useless-escape */
import { GridColDef } from '@mui/x-data-grid';
import { ApexOptions } from 'apexcharts';

export const replaceNtoBr = (text: string): string => {
  if (!text) return '';
  text = text.replace(/(application:)\n(Step \d+:)/g, '$1\n\n$2');
  text = text.replace(/(Step \d+:.*)\n/g, '$1\n\n');
  text = text.replace(/\n\s*\n\s*(?=###)/g, '\n\n');
  text = text.replace(/^(### )(.+)$/gm, '$1**$2**');
  text = text.replace(/^(### )(.+)$/gm, '$1**$2**');
  text = text.replace(/^(Explanation:)/gm, '**$1**');
  text = text.replace(
    /```([\s\S]*?)```/g,
    match => `\`\`\`${match.slice(3, -3).trim()}\`\`\``,
  );
  text = text.replace(/`([^`]+)`/g, '`$1`');
  text = text.replace(/^\s*[\-\*]\s+/gm, '* ');
  text = text.replace(/(\*\*.*\*\*)\s*\n/g, '$1\n\n');
  return text.trim();
};

export const splitTextIntoSegments = (
  text: string,
): Array<{
  type: 'code' | 'text' | 'table' | 'chart' | 'img' | 'audio' | 'link' | 'mermaid';
  content: string;
  isHtml?: boolean;
  title?: string;
}> => {
  if (!text) return [{ type: 'text', content: '' }];

  const segments: Array<{
    type: 'code' | 'text' | 'table' | 'chart' | 'img' | 'audio' | 'link' | 'mermaid';
    content: string;
    isHtml?: boolean;
    title?: string;
  }> = [];

  const combinedRegex = /(?:```([\w]*)\n([\s\S]*?)```)|(\|(?:.*\|)+\n\|[-:| ]+\|\n(?:\|.*\|[\n]*)+)|(<img\s.*?>)|(<audio\s.*?<\/audio>)|((?:https?:\/\/|www\.)[^\s/$.?#].[^\s]*)|(<!DOCTYPE html>[\s\S]*?(?=```|$))/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const plainText = text.slice(lastIndex, match.index).trim();
      if (plainText) {
        segments.push({
          type: 'text',
          content: plainText,
        });
      }
    }

    if (match[1] !== undefined) {
      const codeContent = match[2];
      const lines = codeContent.split('\n').filter(line => line.trim() !== '');

      console.log('match[1]', match[1]);
      
      if (lines.length === 1) {
        segments.push({
          type: 'code',
          content: lines[0],
          isHtml: /<\/?[a-z][\s\S]*>/i.test(lines[0]),
          title: '',
        });
      } else {
        const codeContent = lines.join('\n').trim();
        const isHtml = /<\/?[a-z][\s\S]*>/i.test(codeContent);

        segments.push({
          type: match[1] === 'mermaid' ? 'mermaid' : 'code',
          content: codeContent,
          isHtml,
          title: '',
        });
      }
    } else if (match[3] !== undefined) {
      segments.push({
        type: 'table',
        content: match[3].trim(),
      });
    } else if (match[4] !== undefined) {
      segments.push({
        type: 'img',
        content: match[4].trim(),
      });
    } else if (match[5] !== undefined) {
      segments.push({
        type: 'audio',
        content: match[5].trim(),
      });
    } else if (match[6] !== undefined) {
      let cleanUrl = match[6].replace(/[\[\]\(\)`"""''\)\.,;]+$/g, '');
      segments.push({
        type: 'link',
        content: extractUrlFromMarkdown(cleanUrl) || cleanUrl,
      });
    } else if (match[7] !== undefined) {
      segments.push({
        type: 'code',
        content: match[7].trim(),
        isHtml: true,
      });
    }

    lastIndex = combinedRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex).trim();
    if (remainingText) {
      segments.push({
        type: 'text',
        content: remainingText,
      });
    }
  }

  return segments;
};

export const processTextSegments = (
  text: string,
): Array<{
  type: 'code' | 'text' | 'table' | 'chart' | 'img' | 'audio' | 'link' | 'mermaid';
  content: string;
}> => {
  if (!text) return [{ type: 'text', content: '' }];

  const segments = splitTextIntoSegments(text);

  const tableSegment = segments.find(segment => segment.type === 'table');

  if (tableSegment) {
    return segments
      .map(segment => {
        if (segment.type === 'text') {
          return { ...segment, content: replaceNtoBr(segment.content) };
        }
        return segment;
      })
      .filter(segment => segment.type !== 'table')
      .concat([tableSegment]);
  }

  return segments.map(segment => {
    if (segment.type === 'text') {
      return { ...segment, content: replaceNtoBr(segment.content) };
    }
    return segment;
  });
};

export const generateDataGridFromTable = (tableContent: string) => {
  const rows: any[] = [];
  const columns: GridColDef[] = [];

  const lines = tableContent.split('\n').filter(line => line.trim() !== '');
  if (lines.length < 2) return { columns: [], rows: [] };

  const headers = lines[0]
    .split('|')
    .map(header => header.trim())
    .filter(header => header);
  headers.forEach(header => {
    columns.push({ field: header, headerName: header, width: 150 });
  });

  for (let i = 2; i < lines.length; i++) {
    const cells = lines[i]
      .split('|')
      .map(cell => cell.trim())
      .filter(cell => cell);
    const row: any = { id: i - 1 };
    headers.forEach((header, index) => {
      row[header] = cells[index] || '';
    });
    rows.push(row);
  }

  return { columns, rows };
};

export const processChartData = (chartContent: string) => {
  try {
    const parsedData = JSON.parse(chartContent);
    const series = parsedData.series || [{ name: 'Series 1', data: [] }];
    const options: ApexOptions = {
      chart: {
        type: parsedData.type || 'line',
        height: parsedData.height || 350,
      },
      title: {
        text: parsedData.title || 'Chart',
        align: 'left',
      },
      xaxis: {
        categories: parsedData.categories || [],
      },
      yaxis: {
        title: {
          text: parsedData.yAxisTitle || 'Y-axis',
        },
      },
      ...parsedData.options,
    };

    return { series, options };
  } catch (error) {
    console.error('Error processing chart data:', error);
    return {
      series: [{ name: 'Error', data: [] }],
      options: {
        chart: {
          type: 'line',
          height: 350,
        },
        title: {
          text: 'Error',
          align: 'left',
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          title: {
            text: 'Y-axis',
          },
        },
      } as ApexOptions,
    };
  }
};

export const extractImageUrl = (htmlString: string): string => {
  const checkIfIsUrl = (url: string) => {
    const urlRegex =
      /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
    return urlRegex.test(url);
  };

  if (checkIfIsUrl(htmlString) && !htmlString?.includes('src=')) {
    return htmlString;
  }

  const urlMatch = htmlString.match(/src="([^"]+)"/);
  return urlMatch ? urlMatch[1] : '';
};

interface ProcessContentResult {
  jsxContent?: string;
  htmlContent?: string[];
}

export function extractReturnContent(content: string): ProcessContentResult {
  const jsxReturnPattern = /return \(([^]*?)\)/;
  const htmlContentPattern = /<[^>]*>?[^<]*?<\/[^>]*>?/g;
  const jsxMatch = content.match(jsxReturnPattern);
  if (jsxMatch) {
    return { jsxContent: jsxMatch[1].trim() };
  }
  const htmlMatches = content.match(htmlContentPattern);
  if (htmlMatches) {
    return { htmlContent: htmlMatches.map(match => match.trim()) };
  }
  return {};
}

export function extractUrlFromMarkdown(markdownOrText: string): string | null {
  const markdownUrlMatch = markdownOrText.match(
    /\[([^\]]+)\]\((https?:\/\/[^\)\s]+)\)/,
  );
  if (markdownUrlMatch && markdownUrlMatch[1]) {
    return markdownUrlMatch[1];
  }
  const httpsUrlMatch = markdownOrText.match(/https?:\/\/[^\s]+/);
  if (httpsUrlMatch && httpsUrlMatch[0]) {
    return httpsUrlMatch[0];
  }
  return null;
}
